import { createAsyncThunk, createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../store';
import HouseholdMemberService from '../../../api/services/HouseholdMemberService';
import { Household } from '../types';

const ACTIONS_PREFIX = 'householdMember';

type WritableHousehold = Draft<Household>;

type HouseholdMemberState = {
  isLoading: boolean;
  error: string | null;
  successMessage: string | null;
  household: WritableHousehold | null;
  searchResults: Draft<fhir4.Person[]> | null;
};

const initialState: HouseholdMemberState = {
  isLoading: false,
  error: null,
  successMessage: null,
  household: null,
  searchResults: null,
};

export const getHouseholdMembersThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/getHouseholdMembers`,
  async (params: { uuid?: string; accessToken: string }, { rejectWithValue }) => {
    const { uuid, accessToken } = params;
    try {
      const response = await HouseholdMemberService.getHouseholdMembers(uuid, accessToken);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addHouseholdMemberThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/addHouseholdMember`,
  async (params: { memberData: any; accessToken: string }, { rejectWithValue }) => {
    const { memberData, accessToken } = params;
    try {
      const response = await HouseholdMemberService.addHouseholdMember(memberData, accessToken);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const removeHouseholdMemberThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/removeHouseholdMember`,
  async (params: { memberData: any; accessToken: string }, { rejectWithValue }) => {
    const { memberData, accessToken } = params;
    try {
      const response = await HouseholdMemberService.updateHouseholdMember(memberData, accessToken);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateHouseholdMemberThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/updateHouseholdMember`,
  async (
    params: { memberParams: any; patientParams: any; accessToken: string },
    { rejectWithValue },
  ) => {
    const { memberParams, patientParams, accessToken } = params;
    try {
      await HouseholdMemberService.updateHouseholdMember(memberParams, accessToken);
      const response = await HouseholdMemberService.updatePatient(patientParams, accessToken);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const householdMemberSlice = createSlice({
  name: 'householdMember',
  initialState,
  reducers: {
    clearHouseholdMemberData: (state) => {
      state.household = null;
      state.searchResults = null;
      state.successMessage = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getHouseholdMembersThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getHouseholdMembersThunk.fulfilled,
        (state, action: PayloadAction<Household | undefined>) => {
          state.isLoading = false;
          state.household = action.payload ? (action.payload as WritableHousehold) : null;
          state.error = null;
        },
      )
      .addCase(getHouseholdMembersThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to get household members';
      })
      .addCase(addHouseholdMemberThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(addHouseholdMemberThunk.fulfilled, (state) => {
        state.isLoading = false;
        state.successMessage = 'Household member added successfully';
        state.error = null;
      })
      .addCase(addHouseholdMemberThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to add household member';
      })
      .addCase(removeHouseholdMemberThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(removeHouseholdMemberThunk.fulfilled, (state) => {
        state.isLoading = false;
        state.successMessage = 'Household member removed successfully';
        state.error = null;
      })
      .addCase(removeHouseholdMemberThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to remove household member';
      })
      .addCase(updateHouseholdMemberThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateHouseholdMemberThunk.fulfilled, (state) => {
        state.isLoading = false;
        state.successMessage = 'Household member updated successfully';
        state.error = null;
      })
      .addCase(updateHouseholdMemberThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to update household member';
      });
  },
});

export const { clearHouseholdMemberData } = householdMemberSlice.actions;

export const selectHouseholdMember = (state: RootState) => state.householdMember;

export default householdMemberSlice.reducer;
